// Style Imports
import Styles from './TerminalPane.module.scss';
import '@xterm/xterm/css/xterm.css';
// React Imports
import { useEffect, useRef } from 'react';
// Module Imports
import { Terminal as XTerm } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
// Context Imports
import { useEditor } from '../../contexts';

const TerminalPane = ({ showToolsPane, showInstructions, resetActivityTimer }) => {
    const { socket, addCommandToHistory, setUserCommandInput } = useEditor();
    const terminalRef = useRef(null);
    const xtermRef = useRef(null);
    const fitAddonRef = useRef(null);

    // Ref to track input locally
    const currentInputRef = useRef('');
    const cursorPositionRef = useRef(0);

    // Function to handle terminal fitting and size updates
    const fitTerminal = () => {
        if (fitAddonRef.current && xtermRef.current && terminalRef.current) {
            // Force a reflow by reading and setting display
            const currentDisplay = terminalRef.current.style.display;
            terminalRef.current.style.display = 'none';
            // Trigger reflow by accessing offsetHeight and storing it
            const height = terminalRef.current.offsetHeight;
            terminalRef.current.style.display = currentDisplay;

            // Wait for next frame to ensure DOM updates are complete
            requestAnimationFrame(() => {
                if (fitAddonRef.current && xtermRef.current) {
                    fitAddonRef.current.fit();
                    const { rows, cols } = xtermRef.current;
                    socket.emit('terminal:resize', { rows, cols });
                }
            });
        }
    };

    const forbiddenKeys = [
        '\t', '\x1b[H', '\u001b[A', '\u001b[B',
        '\x1b[F', '\u0001', '\u0002', '\u0004', '\u0008',
        '\u0012', '\u0005', '\u0003', '\u0010', '\u000C', '\u000A',
        '\u001b[1;5D', '\u001b[1;5C', '\u001b[1;2D', '\u001b[1;2C',
        '\u001b[1;2A', '\u001b[1;2B', '\u001b[1;2H', '\u001b[1;2F',
        '\u001b[1;2P', '\u001b[1;2Q', '\u001b[1;2R', '\u001b[1;2S',
        '\x1b[15;2~'
    ];

    // Initialize terminal
    useEffect(() => {
        if (terminalRef.current) {
            xtermRef.current = new XTerm({
                cursorBlink: true,
                fontSize: 14,
                altClickMovesCursor: false,
                theme: {
                    background: '#0C1F27',
                },
            });

            fitAddonRef.current = new FitAddon();
            xtermRef.current.loadAddon(fitAddonRef.current);
            xtermRef.current.open(terminalRef.current);

            const xtermViewport = terminalRef.current.querySelector('.xterm-viewport');
            if (xtermViewport) {
                xtermViewport.style.overflow = 'hidden';
                xtermViewport.style.overflowY = 'hidden';
            }
            // Initial fit with slight delay to ensure DOM is ready
            setTimeout(fitTerminal, 100);

            // Handle terminal input
            xtermRef.current.onData((data) => {
                resetActivityTimer();
                // Check if the input is Enter key
                if (data === '\r') {
                    // Set userInput only when Enter is pressed
                    addCommandToHistory(currentInputRef.current);
                    setUserCommandInput(currentInputRef.current);
                    // Reset the current input ref and cursor position
                    currentInputRef.current = '';
                    cursorPositionRef.current = 0;
                } else if (data === '\u001b[D') {
                    // Left arrow key - move cursor left
                    if (cursorPositionRef.current > 0) {
                        cursorPositionRef.current--;
                    }
                    // Send to nodepty for terminal handling
                    socket.emit('terminal:input', data);
                    return;
                } else if (data === '\u001b[C') {
                    // Right arrow key - move cursor right
                    if (cursorPositionRef.current < currentInputRef.current.length) {
                        cursorPositionRef.current++;
                    }
                    // Send to nodepty for terminal handling
                    socket.emit('terminal:input', data);
                    return;
                } else if (forbiddenKeys.includes(data)) {
                    // Ignore forbidden characters
                    return;
                } else if (data === '\u007F') {
                    // Handling backspace
                    if (cursorPositionRef.current > 0) {
                        const input = currentInputRef.current;
                        currentInputRef.current =
                            input.slice(0, cursorPositionRef.current - 1) +
                            input.slice(cursorPositionRef.current);
                        cursorPositionRef.current--;
                    }
                } else if (data === '\u001b[3~') {
                    // Delete key - delete character at cursor position
                    if (cursorPositionRef.current < currentInputRef.current.length) {
                        const input = currentInputRef.current;
                        currentInputRef.current =
                            input.slice(0, cursorPositionRef.current) +
                            input.slice(cursorPositionRef.current + 1);
                    }
                    // Send to nodepty for terminal handling
                    socket.emit('terminal:input', data);
                    return;
                } else {
                    // Insert character at cursor position
                    const input = currentInputRef.current;
                    currentInputRef.current =
                        input.slice(0, cursorPositionRef.current) +
                        data +
                        input.slice(cursorPositionRef.current);
                    cursorPositionRef.current += data.length;
                }

                // Always emit input to socket
                socket.emit('terminal:input', data);
            });

            // Handle terminal output
            socket.on('terminal:output', (data) => {
                if (xtermRef.current) {
                    xtermRef.current.write(data);
                }
            });

            // Cleanup
            return () => {
                if (xtermRef.current) {
                    xtermRef.current.dispose();
                }
                socket.disconnect();
            };
        }
    }, [socket]);

    // Handle window resize
    useEffect(() => {
        // Only add resize listener if terminal is shown
        if (showToolsPane) {
            const handleResize = () => {
                requestAnimationFrame(fitTerminal);
            };

            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }
    }, [showToolsPane]);


    // Handle instructions toggle and tools pane visibility changes
    useEffect(() => {
        if (showToolsPane) {
            const resizeTimeout = setTimeout(() => {
                fitTerminal();
            }, 150);
            return () => clearTimeout(resizeTimeout);
        }
    }, [showInstructions, showToolsPane]);

    return (
        <div
            id='terminalPane'
            className={Styles.terminalContainer}
            style={{
                height: '100%',
                width: '100%'
            }}
            ref={terminalRef}
        />
    );
};

export default TerminalPane;



